/**
 *
 *  Copyright (c) "Neo4j"
 *  Neo4j Sweden AB [http://neo4j.com]
 *
 *  This file is part of Neo4j.
 *
 *  Neo4j is free software: you can redistribute it and/or modify
 *  it under the terms of the GNU General Public License as published by
 *  the Free Software Foundation, either version 3 of the License, or
 *  (at your option) any later version.
 *
 *  This program is distributed in the hope that it will be useful,
 *  but WITHOUT ANY WARRANTY; without even the implied warranty of
 *  MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 *  GNU General Public License for more details.
 *
 *  You should have received a copy of the GNU General Public License
 *  along with this program.  If not, see <http://www.gnu.org/licenses/>.
 */
import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useDismiss,
  useFloating,
  useInteractions,
  useMergeRefs,
  useRole,
} from '@floating-ui/react';
import classNames from 'classnames';
import { Resizable, type ResizableProps } from 're-resizable';
import type React from 'react';
import {
  type ComponentProps,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type HtmlAttributes,
  type PolymorphicCommonProps,
} from '../_common/types';
import { needleWarningMessage } from '../_common/utils';
import { CleanIconButton } from '../clean-icon-button';
import { ConditionalWrap } from '../conditional-wrap';
import { XMarkIconOutline } from '../icons/generated/heroIcons/XMarkIconOutline';
import { Typography } from '../typography';

type DrawerPosition = 'left' | 'right';

type DrawerType = 'overlay' | 'push' | 'modal';

/**
 * @remarks
 * Where `ResizableProps` is the props for the `re-resizable` library. For more information, see the [re-resizable documentation](https://github.com/bokuweb/re-resizable).
 *
 * @remarks
 * Where `FloatingPortalProps` is the props for the `@floating-ui/react` library. For more information, see the [FloatingPortal documentation](https://floating-ui.com/docs/floatingportal).
 */
type DrawerProps = {
  /** Controls whether the drawer is open (expanded) or closed (collapsed). */
  isExpanded: boolean;

  /** Callback fired when the drawer's expanded state changes (close button, Escape, click outside, or programmatic change). */
  onExpandedChange?: (expanded: boolean) => void;

  /** Position where the drawer appears on screen. By default, it appears from the left. */
  position?: DrawerPosition;

  /** How the drawer affects the layout of sibling content. By default, it overlays content. */
  type?: DrawerType;

  /** Enables horizontal resizing of the drawer, by default it is not resizable. */
  isResizeable?: boolean;

  /** To use this prop the isResizeable prop must be set to true. For overriding the third party library props: https://github.com/bokuweb/re-resizable
   * The property enable has default values set for bottom, bottomLeft etc based on the position prop.
   */
  resizeableProps?: ResizableProps;

  /** Whether a close button (X) is displayed in the drawer. */
  isCloseable?: boolean;

  /** Whether to portal the content. Only applies when type is "overlay". Type "modal" is always portaled.*/
  isPortaled?: boolean;

  // TODO v5: this should maybe be default on portaled in the future
  /**
   * Whether pressing Escape closes the drawer. Should be set to true for overlay drawers.
   * - When type is "modal", this defaults to true.
   * - When type is "overlay", this defaults to false.
   * - When type is "push", no effect.
   */
  closeOnEscape?: boolean;

  // TODO v5: this should maybe be default on overlay in the future
  /**
   * When type is "overlay", whether clicking outside the drawer closes it. Should be set to true for overlay drawers.
   * - When type is "overlay" or "modal", this defaults to false.
   * - When type is "push", no effect.
   */
  closeOnClickOutside?: boolean;

  // TODO v5: remove this prop
  /**
   *  @deprecated overlay are now focus trapped by default, this prop has no effect anymore.
   */
  closeOnFocusOut?: boolean;

  /** The content to display within the drawer. */
  children: React.ReactNode;

  /**
   * Props to pass to the portal from @floating-ui/react FloatingPortal
   * Only applies when type is "overlay" and isPortaled is true, or if type is "modal".
   * @see https://floating-ui.com/docs/floatingportal
   */
  portalProps?: ComponentProps<typeof FloatingPortal>;
} & (
  | {
      type: 'modal';
      /** The aria-label to apply to the drawer. Required for accessibility when type is "modal" or "overlay".*/
      ariaLabel: string;
    }
  | {
      type?: 'push' | 'overlay';
      // TODO v5: aria label should be required when type is "overlay" too.
      ariaLabel?: string;
    }
);

const KEYBOARD_RESIZE_STEP_PX = 16;

function parsePixelSize(
  value: string | number | undefined,
): number | undefined {
  if (value === undefined) {
    return undefined;
  }
  if (typeof value === 'number') {
    return value;
  }
  if (/^\d+(\.\d+)?px$/i.test(value)) {
    return parseFloat(value);
  }
  return undefined;
}

function parsePercentage(
  value: string | number | undefined,
  referenceWidth: number | undefined,
): number | undefined {
  if (
    typeof value !== 'string' ||
    !value.endsWith('%') ||
    referenceWidth === undefined
  ) {
    return undefined;
  }
  const percent = parseFloat(value);
  return Number.isNaN(percent) ? undefined : (percent / 100) * referenceWidth;
}

type DrawerResizeHandleProps = {
  /** Which edge this handle resizes (right = drawer’s right edge, left = drawer’s left edge). */
  handleSide: 'left' | 'right';
  /** Called with a pixel delta to apply to the drawer width (positive = wider). */
  onResizeBy: (delta: number) => void;
  onKeyDown?: React.KeyboardEventHandler<HTMLDivElement>;
  /** Current width in pixels (for aria-valuenow). */
  valueNow?: number;
  /** Minimum width in pixels (for aria-valuemin). */
  valueMin?: number;
  /** Maximum width in pixels (for aria-valuemax). */
  valueMax?: number;
};

function DrawerResizeHandle({
  handleSide,
  onResizeBy,
  valueMax,
  valueMin,
  valueNow,
}: DrawerResizeHandleProps) {
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      const isArrowKey = e.key === 'ArrowLeft' || e.key === 'ArrowRight';
      if (isArrowKey) {
        e.preventDefault();
        const delta =
          handleSide === 'right'
            ? e.key === 'ArrowRight'
              ? KEYBOARD_RESIZE_STEP_PX
              : -KEYBOARD_RESIZE_STEP_PX
            : e.key === 'ArrowLeft'
              ? KEYBOARD_RESIZE_STEP_PX
              : -KEYBOARD_RESIZE_STEP_PX;
        onResizeBy(delta);
      }
    },
    [handleSide, onResizeBy],
  );

  return (
    /* eslint-disable-next-line jsx-a11y/no-noninteractive-element-interactions -- Resize handle is focusable for keyboard resize. */
    <div
      aria-label={`Resize drawer with arrow keys. Handle on ${handleSide}.`}
      aria-orientation="vertical"
      aria-valuemax={valueMax}
      aria-valuemin={valueMin}
      aria-valuenow={valueNow ?? 0}
      aria-valuetext={`drawer width ${valueNow ?? 0}px`}
      className="ndl-drawer-resize-handle"
      data-drawer-handle={handleSide}
      onKeyDown={handleKeyDown}
      role="separator"
      style={{ height: '100%', width: '100%' }}
      // eslint-disable-next-line jsx-a11y/no-noninteractive-tabindex -- Resize handle is focusable for keyboard resize.
      tabIndex={0}
    />
  );
}

const DrawerComponent = function DrawerComponent<
  T extends React.ElementType = 'div',
>({
  children,
  className = '',
  isExpanded,
  onExpandedChange,
  position = 'left',
  type = 'overlay',
  isResizeable = false,
  resizeableProps,
  isCloseable = true,
  isPortaled = false,
  portalProps = {},
  closeOnEscape = type === 'modal' ? true : false,
  closeOnClickOutside = false,
  ariaLabel,
  htmlAttributes,
  style,
  ref,
  as,
  ...restProps
}: PolymorphicCommonProps<T, DrawerProps>) {
  const resizableRef = useRef<Resizable | null>(null);
  const [resizeWidth, setResizeWidth] = useState<number>(0);

  if ((type === 'modal' || type === 'overlay') && !ariaLabel) {
    needleWarningMessage(
      'A Drawer should have an aria-label when type is "modal" or "overlay" to be accessible.',
    );
  }

  const { refs, context } = useFloating({
    onOpenChange: onExpandedChange,
    open: isExpanded,
  });

  /** Dismiss (escape + click outside) applies to portaled overlay and to non-portaled overlay. */
  const shouldUseDismiss =
    type === 'modal' ||
    (type === 'overlay' && !isPortaled && isExpanded) ||
    (type === 'overlay' && isPortaled);

  const dismiss = useDismiss(context, {
    enabled: shouldUseDismiss,
    escapeKey: closeOnEscape && type !== 'push',
    outsidePress: closeOnClickOutside && type !== 'push',
  });
  const role = useRole(context, {
    enabled: type === 'modal' || type === 'overlay',
    role: 'dialog',
  });
  const { getFloatingProps } = useInteractions([dismiss, role]);

  const resizableMergedRef = useMergeRefs([resizableRef, ref]);

  const handleResizeBy = useCallback(
    (delta: number) => {
      if (!resizableRef.current) {
        return;
      }
      const current = resizableRef.current.size;
      const parentWidth =
        resizableRef.current.resizable?.parentElement?.offsetWidth;
      const min =
        parsePixelSize(resizeableProps?.minWidth) ??
        parsePercentage(resizeableProps?.minWidth, parentWidth);
      const max =
        parsePixelSize(resizeableProps?.maxWidth) ??
        parsePercentage(resizeableProps?.maxWidth, parentWidth);
      const newWidth = Math.max(
        min ?? 0,
        Math.min(max ?? Number.POSITIVE_INFINITY, current.width + delta),
      );
      resizableRef.current.updateSize({
        height: '100%',
        width: newWidth,
      });
      setResizeWidth(newWidth);
    },
    [resizeableProps?.minWidth, resizeableProps?.maxWidth],
  );

  const handleResize = useCallback(
    (
      event: MouseEvent | TouchEvent,
      direction: Parameters<NonNullable<ResizableProps['onResize']>>[1],
      elementRef: HTMLElement,
      delta: { width: number; height: number },
    ) => {
      setResizeWidth(elementRef.offsetWidth);
      resizeableProps?.onResize?.(event, direction, elementRef, delta);
    },
    [resizeableProps],
  );

  useEffect(() => {
    if (!isResizeable || !resizableRef.current) {
      return;
    }
    const width = resizableRef.current.size.width;
    if (width > 0) {
      setResizeWidth(width);
    }
  }, [isResizeable]);

  const classes = classNames('ndl-drawer', className, {
    'ndl-drawer-expanded': isExpanded,
    'ndl-drawer-left': position === 'left',
    'ndl-drawer-modal': type === 'modal',
    'ndl-drawer-overlay': type === 'overlay',
    'ndl-drawer-portaled': isPortaled && type === 'overlay',
    'ndl-drawer-push': type === 'push',
    'ndl-drawer-right': position === 'right',
  });

  const resizableStylePosition = type === 'overlay' ? 'absolute' : 'relative';
  const Component = as ?? 'div';

  const handleClose = useCallback(() => {
    onExpandedChange?.(false);
  }, [onExpandedChange]);

  const closeButton = useMemo(
    () =>
      isCloseable || type === 'modal' ? (
        <CleanIconButton
          className="ndl-drawer-close-button"
          onClick={handleClose}
          description={null}
          size="medium"
          htmlAttributes={{
            'aria-label': 'Close',
          }}
        >
          <XMarkIconOutline />
        </CleanIconButton>
      ) : null,
    [handleClose, isCloseable, type],
  );

  const resizableContent = (
    <Resizable
      as={Component}
      defaultSize={{
        height: '100%',
        width: 'auto',
      }}
      {...resizeableProps}
      className={classes}
      style={{
        position: resizableStylePosition,
        ...style,
        ...resizeableProps?.style,
      }}
      boundsByDirection={true}
      bounds={resizeableProps?.bounds ?? 'parent'}
      handleStyles={{
        /*
         * Small offset so the handle is easier to hit when the drawer content is scrollable.
         */
        ...(position === 'left' && { right: { right: '-8px' } }),
        ...(position === 'right' && { left: { left: '-8px' } }),
      }}
      enable={{
        bottom: false,
        bottomLeft: false,
        bottomRight: false,
        left: position === 'right',
        right: position === 'left',
        top: false,
        topLeft: false,
        topRight: false,
      }}
      handleComponent={
        position === 'left'
          ? {
              right: (
                <DrawerResizeHandle
                  handleSide="right"
                  onResizeBy={handleResizeBy}
                  valueMax={parsePixelSize(resizeableProps?.maxWidth)}
                  valueMin={parsePixelSize(resizeableProps?.minWidth)}
                  valueNow={resizeWidth}
                />
              ),
            }
          : {
              left: (
                <DrawerResizeHandle
                  handleSide="left"
                  onResizeBy={handleResizeBy}
                  valueMax={parsePixelSize(resizeableProps?.maxWidth)}
                  valueMin={parsePixelSize(resizeableProps?.minWidth)}
                  valueNow={resizeWidth}
                />
              ),
            }
      }
      onResize={handleResize}
      ref={resizableMergedRef}
      {...restProps}
      {...htmlAttributes}
    >
      {closeButton}
      {children}
    </Resizable>
  );

  const regularContent = (
    <Component
      className={classNames(classes)}
      style={style}
      ref={ref}
      {...restProps}
      {...htmlAttributes}
    >
      {closeButton}
      {children}
    </Component>
  );

  /** what content to render*/
  const content = isResizeable ? resizableContent : regularContent;

  // const floatingRefs = useMergeRefs([refs.setFloating, refs.setReference]);

  /** modal behavior */
  if (type === 'modal' && isExpanded) {
    return (
      <FloatingPortal {...portalProps}>
        <FloatingOverlay
          className="ndl-drawer-overlay-root"
          lockScroll={true}
        />
        <FloatingFocusManager context={context} modal={true} returnFocus={true}>
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            aria-label={ariaLabel}
            aria-modal="true"
          >
            {content}
          </div>
        </FloatingFocusManager>
      </FloatingPortal>
    );
  }

  if (type === 'overlay' && isExpanded) {
    return (
      <ConditionalWrap
        shouldWrap={isPortaled}
        wrap={(wrapChildren) => (
          <FloatingPortal preserveTabOrder={true} {...portalProps}>
            {wrapChildren}
          </FloatingPortal>
        )}
      >
        <FloatingFocusManager
          disabled={!isExpanded}
          context={context}
          modal={true}
          returnFocus={true}
        >
          <div
            ref={refs.setFloating}
            {...getFloatingProps()}
            aria-label={ariaLabel}
            aria-modal="true"
          >
            {content}
          </div>
        </FloatingFocusManager>
      </ConditionalWrap>
    );
  }

  return content;
};

DrawerComponent.displayName = 'Drawer';

type DrawerHeaderProps = {
  children: React.ReactNode;
  className?: string;
  // TODO v5: fix the typing on the Drawer subcomponents to use the common props type. Also, this is not an h5 haha. Should be polymorphic I guess, maybe default to h2 as we've recommended.
  htmlAttributes?: HtmlAttributes<'h5'>;
};

const DrawerHeader = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerHeaderProps) => {
  const classes = classNames('ndl-drawer-header', className);

  if (typeof children === 'string' || typeof children === 'number') {
    return (
      <Typography
        className={classes}
        variant="title-3"
        {...restProps}
        {...htmlAttributes}
      >
        {children}
      </Typography>
    );
  }

  return (
    <div className={classes} {...restProps} {...htmlAttributes}>
      {children}
    </div>
  );
};

type DrawerActionsProps = {
  children: React.ReactNode;
  className?: string;
  htmlAttributes?: HtmlAttributes<'div'>;
};

const DrawerActions = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerActionsProps) => {
  const classes = classNames('ndl-drawer-actions', className);

  return (
    <div className={classes} {...restProps} {...htmlAttributes}>
      {children}
    </div>
  );
};

type DrawerBodyProps = {
  children: React.ReactNode;
  className?: string;
  htmlAttributes?: HtmlAttributes<'div'>;
};

const DrawerBody = ({
  children,
  className = '',
  htmlAttributes,
  ...restProps
}: DrawerBodyProps) => {
  const classes = classNames('ndl-drawer-body', className);

  return (
    <div className="ndl-drawer-body-wrapper">
      <div className={classes} {...restProps} {...htmlAttributes}>
        {children}
      </div>
    </div>
  );
};

const Drawer = Object.assign(DrawerComponent, {
  Actions: DrawerActions,
  Body: DrawerBody,
  Header: DrawerHeader,
});

export { Drawer };
